import Grid from '@material-ui/core/Grid';
import PeButton from '@pe-libs/react-components/build/components/PeButton';
import PeFormCheckbox from '@pe-libs/react-components/build/components/PeFormCheckbox';
import PeFormTextField from '@pe-libs/react-components/build/components/PeFormTextField';
import PeLink from '@pe-libs/react-components/build/components/PeLink';
import PeTypography from '@pe-libs/react-components/build/components/PeTypography';
import useIntlMessage from '@pe-libs/react-components/build/lib/intl/hooks/useIntlMessage';
import { useFormik } from 'formik';
import { useRouter } from 'next/router';
import React, { FC } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import useRedirect from '#hooks/useRedirect';
import { referralFormRequested } from '#modules/defaultLandingReferral/actions/form';
import { validationSchema } from '#modules/defaultLandingReferral/schemas';
import { ThunkAppDispatch } from '../../../../types';
import messages from './messages';
import useStyles from './styles';

type Props = {
  profileTitle: string;
};

const LandingForm: FC<Props> = ({ profileTitle }) => {
  const styles = useStyles();
  const formatMessage = useIntlMessage();
  const { urlToLivePage } = useRedirect();
  const dispatch = useDispatch<ThunkAppDispatch>();
  const { query } = useRouter();

  const successMessage = formatMessage(messages.formSubmitSuccess);

  const formik = useFormik({
    initialValues: {
      consulting: false,
      newsletter: false,
      others: false,
      comment: '',
      name: '',
      surname: '',
      email: '',
      telephone: '',
    },
    validationSchema,
    onSubmit: (values, actions) => {
      dispatch(
        referralFormRequested({
          type: 'reviewer',
          profile: `/v2/profiles/${String(query.profile ?? query.profileId)}`,
          consulting: values.consulting,
          newsletter: values.newsletter,
          others: values.others,
          comment: values.comment,
          firstName: values.name,
          lastName: values.surname,
          emailAddress: values.email,
          phoneNumber: values.telephone,
          invitationUrl: query.invitation && String(query.invitation),
          reviewId: String(query.id),
        }),
      )
        .unwrap()
        .then(() => {
          toast.success(successMessage);
        });

      actions.resetForm();
    },
  });

  return (
    <div className={styles.container}>
      <PeTypography variant="h3" className={styles.mainTitle}>
        {formatMessage(messages.formTitle, { company: profileTitle })}
      </PeTypography>
      <PeTypography variant="body2" className={styles.subtitle}>
        {formatMessage(messages.formSubtitle, { company: profileTitle })}
      </PeTypography>
      <form onSubmit={formik.handleSubmit}>
        <Grid container={true} spacing={2} className={styles.formSection}>
          <div className={styles.checkboxSection}>
            <PeFormCheckbox formik={formik} name="consulting" checkboxLabel={formatMessage(messages.consulting)} />
            <PeFormCheckbox formik={formik} name="newsletter" checkboxLabel={formatMessage(messages.newsletter)} />
            <PeFormCheckbox formik={formik} name="others" checkboxLabel={formatMessage(messages.others)} />
          </div>
          <Grid item={true} xs={12}>
            <PeFormTextField formik={formik} name="comment" placeholder={formatMessage(messages.comment)} />
          </Grid>
        </Grid>
        <Grid container={true} spacing={5}>
          <Grid item={true} xs={12} md={6}>
            <PeTypography variant="body2" className={styles.label}>
              {formatMessage(messages.labelName)}
            </PeTypography>
            <PeFormTextField formik={formik} name="name" />
          </Grid>
          <Grid item={true} xs={12} md={6}>
            <PeTypography variant="body2" className={styles.label}>
              {formatMessage(messages.labelSurname)}
            </PeTypography>
            <PeFormTextField formik={formik} name="surname" />
          </Grid>
          <Grid item={true} xs={12}>
            <PeTypography variant="body2" className={styles.label}>
              {formatMessage(messages.labelEmail)}
            </PeTypography>
            <PeFormTextField formik={formik} name="email" />
          </Grid>
          <Grid item={true} xs={12}>
            <PeTypography variant="body2" className={styles.label}>
              {formatMessage(messages.labelTelephone)}
            </PeTypography>
            <PeFormTextField formik={formik} name="telephone" />
          </Grid>
        </Grid>
        <div className={styles.rulesText}>
          {formatMessage(messages.rules, {
            link: (
              <PeLink target="_blank" href={urlToLivePage('privacyPolicy')}>
                {formatMessage(messages.rulesLink)}
              </PeLink>
            ),
          })}
        </div>
        <div className={styles.submitContainer}>
          <PeButton
            type="submit"
            variant="contained"
            color="warning"
            aria-label={`${formatMessage(messages.submit)} button`}
          >
            {formatMessage(messages.submit)}
          </PeButton>
        </div>
      </form>
    </div>
  );
};

export default LandingForm;
